import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { Globe, Users, TrendingUp } from 'lucide-react';
import marketVisionBg from '../../assets/pitch-deck/market-vision-background.png';

const MarketVisionSection: React.FC = () => {
  return (
    <section className="py-32 bg-theme-surface border-y border-theme-border relative overflow-hidden">
      {/* Background image overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <img 
            src={marketVisionBg} 
            alt="Market background"
            className="w-full h-full object-cover"
        />
      </div>

      <Container className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-7xl font-black text-theme-text-primary mb-8 leading-tight">
              Dominating the <br /> 
              <span className="gradient-heading">Longevity Economy.</span>
            </h2>
            <p className="text-xl text-theme-text-secondary mb-12 leading-relaxed font-light">
              We aren't just building a company; we are building health infrastructure for a world where 100-year lives are the baseline. 
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-black text-theme-text-primary mb-2">$25T</div>
                <div className="text-xs uppercase tracking-widest text-theme-text-secondary font-bold">Market Potential</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-theme-text-primary mb-2">2B+</div>
                <div className="text-xs uppercase tracking-widest text-theme-text-secondary font-bold">Aging Population</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-theme-text-primary mb-2">91%</div>
                <div className="text-xs uppercase tracking-widest text-theme-text-secondary font-bold">Consumer Demand</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="bg-theme-bg backdrop-blur-md p-8 rounded-[32px] border border-theme-border shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-theme-primary/10 flex items-center justify-center">
                        <Globe className="w-6 h-6 text-theme-primary" />
                    </div>
                    <h4 className="text-xl font-bold text-theme-text-primary">Global Scalability</h4>
                </div>
                <p className="text-theme-text-secondary leading-relaxed">Our partner lab network and digital infrastructure allow us to scale across borders without heavy local capex.</p>
            </div>

            <div className="bg-theme-bg backdrop-blur-md p-8 rounded-[32px] border border-theme-border shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-theme-success/10 flex items-center justify-center">
                        <Users className="w-6 h-6 text-theme-success" />
                    </div>
                    <h4 className="text-xl font-bold text-theme-text-primary">Compound Data Effect</h4>
                </div>
                <p className="text-theme-text-secondary leading-relaxed">The more users we have, the more precise our AI recommendation engine becomes, creating a self-reinforcing flywheel.</p>
            </div>

            <div className="bg-theme-bg backdrop-blur-md p-8 rounded-[32px] border border-theme-border shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-theme-warning/10 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-theme-warning" />
                    </div>
                    <h4 className="text-xl font-bold text-theme-text-primary">Future Exit Strategy</h4>
                </div>
                <p className="text-theme-text-secondary leading-relaxed">Positioned as the ultimate acquisition target for Big Tech, Insurance Providers, or Lifestyle Conglomerates.</p>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default MarketVisionSection;
